/**
 * Firebase client setup, shared by every screen in the vendor app.
 *
 * These values are not secrets — a web API key identifies the project, it does
 * not grant access. Access is decided by `firestore.rules` and by the callable
 * functions. They still come from env vars so the admin app and any future app
 * can point at an emulator or a staging project without code edits.
 */
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  connectAuthEmulator,
  type User,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { assertFirebaseEnv } from '@lmac/ui';

assertFirebaseEnv(import.meta.env as unknown as Record<string, string | undefined>);

export const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
});

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

/**
 * Signs the vendor in anonymously and invisibly.
 *
 * Vendors get no account on purpose: a swap meet vendor reserves a table once
 * a year, and a password form is the fastest way to send them back to paper.
 * The anonymous UID exists only so a table hold can be scoped to one browser.
 * Real identity is the email address they type, verified by the confirmation
 * message arriving in their inbox.
 */
export async function ensureSession(): Promise<User> {
  if (auth.currentUser) return auth.currentUser;
  const credential = await signInAnonymously(auth);
  return credential.user;
}
